import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';
import '../styles/BattleshipSpectator.css';

const TEAMS = ['red', 'blue', 'green', 'yellow'];
const TEAM_COLORS = {
  red: '#ff4444',
  blue: '#4444ff',
  green: '#44ff44',
  yellow: '#ffff44'
};
const TEAM_NAMES = {
  red: 'RED',
  blue: 'BLUE',
  green: 'GREEN',
  yellow: 'YELLOW'
};
const GRID_SIZE = 10;
const TOTAL_SHIPS = 5;
const TOTAL_HEALTH = 15; // 5+4+3+2+1

const BattleshipSpectatorPage = ({ compact = false }) => {
  const [searchParams] = useSearchParams();
  const isEmbed = compact || searchParams.get('embed') === 'true';
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const socketRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(null);
  const animTimeoutRef = useRef(null);
  const animPriorityRef = useRef(0);
  const animExpiresRef = useRef(0);
  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Fetch initial game state
  const fetchGameState = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/battleship/spectate`);
      setGameState(response.data.game);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching spectator state:', err);
      setLoading(false);
    }
  }, []);

  // Add event to recent events list
  const addRecentEvent = useCallback((message) => {
    setRecentEvents(prev => {
      const newEvents = [{ message, timestamp: new Date() }, ...prev].slice(0, 8);
      return newEvents;
    });
  }, []);

  // Setup WebSocket connection
  useEffect(() => {
    // Connect to socket
    const socket = io(API_BASE_URL, {
      auth: { public: true },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[Spectator] Connected to server');
      setConnected(true);
      // Register as a battleship board to receive events
      socket.emit('battleship:register');
      // Fetch initial state
      fetchGameState();
    });

    socket.on('disconnect', () => {
      console.log('[Spectator] Disconnected from server');
      setConnected(false);
    });

    // Handle battleship events
    socket.on('battleship-event', (data) => {
      console.log('[Spectator] Event:', data.type, data);
      setLastUpdate(new Date());

      // Priority-aware animation: higher priority events won't be overridden by lower ones
      // 5 = game-over, 4 = eliminated, 3 = sunk, 2 = hit/miss, 1 = turn/start, 0 = none
      const triggerAnim = (label, color, icon, style, duration, priority = 2) => {
        const now = Date.now();
        // Don't let low-priority events override higher ones still displaying
        if (priority < animPriorityRef.current && now < animExpiresRef.current) return;

        animPriorityRef.current = priority;
        const ms = (duration || 3) * 1000;
        animExpiresRef.current = now + ms;
        setActiveAnimation({ label, color, icon, style, key: now });
        if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
        animTimeoutRef.current = setTimeout(() => {
          setActiveAnimation(null);
          animPriorityRef.current = 0;
        }, ms);
      };

      switch (data.type) {
        case 'battleship:game-start':
        case 'battleship:game-loaded':
          addRecentEvent('Game created - Setup phase');
          fetchGameState();
          break;

        case 'battleship:place-ship':
          addRecentEvent(`${data.team.toUpperCase()} placed ${data.ship}`);
          fetchGameState();
          break;

        case 'battleship:hit':
          addRecentEvent(`${data.attackingTeam.toUpperCase()} HIT ${data.targetTeam.toUpperCase()}!`);
          triggerAnim(`${data.attackingTeam.toUpperCase()} HIT ${data.targetTeam.toUpperCase()}!`, '#ff4444', '\u{1F4A5}', 'explode', 4, 2);
          fetchGameState();
          break;

        case 'battleship:miss':
          addRecentEvent(`${data.attackingTeam.toUpperCase()} missed ${data.targetTeam.toUpperCase()}`);
          triggerAnim(`${data.attackingTeam.toUpperCase()} MISS`, '#4488ff', '\u{1F30A}', 'fade', 3, 2);
          fetchGameState();
          break;

        case 'battleship:sunk':
          addRecentEvent(`${data.targetTeam.toUpperCase()}'s ${data.ship} was SUNK!`);
          triggerAnim(`${data.targetTeam.toUpperCase()}'s ${data.ship} SUNK!`, '#ff6600', '\u{1F6A2}', 'shake', 5, 3);
          fetchGameState();
          break;

        case 'battleship:eliminated':
          addRecentEvent(`${data.team.toUpperCase()} has been ELIMINATED!`);
          triggerAnim(`${data.team.toUpperCase()} ELIMINATED!`, '#ff0044', '\u2620\uFE0F', 'explode', 6, 4);
          fetchGameState();
          break;

        case 'battleship:turn':
          // No overlay — status banner already shows whose turn it is
          addRecentEvent(`${(data.currentTurn || '').toUpperCase()}'s turn`);
          fetchGameState();
          break;

        case 'battleship:game-over':
          addRecentEvent(`${data.winner.toUpperCase()} WINS THE GAME!`);
          triggerAnim(`${data.winner.toUpperCase()} WINS!`, '#ffd700', '\u{1F3C6}', 'explode', 8, 5);
          fetchGameState();
          break;

        case 'battleship:game-end':
          addRecentEvent('Game ended');
          setGameState(null);
          break;

        default:
          fetchGameState();
      }
    });

    // Handle scroll-event animations (from admin sprite triggers)
    // These are secondary — the battleship-event handler above is primary.
    // Only trigger if no higher-priority animation is already showing.
    socket.on('scroll-event', (data) => {
      const battleshipEvents = {
        'battleship:hit': { label: 'DIRECT HIT!', color: '#ff4444', icon: '\u{1F4A5}', style: 'explode', priority: 2 },
        'battleship:miss': { label: 'MISS!', color: '#4488ff', icon: '\u{1F30A}', style: 'fade', priority: 2 },
        'battleship:sunk': { label: 'SHIP SUNK!', color: '#ff6600', icon: '\u{1F6A2}', style: 'shake', priority: 3 },
        'battleship:eliminated': { label: 'ELIMINATED!', color: '#ff0044', icon: '\u2620\uFE0F', style: 'explode', priority: 4 },
        'battleship:game-over': { label: 'VICTORY!', color: '#ffd700', icon: '\u{1F3C6}', style: 'explode', priority: 5 },
        'sprite:sinking-ship': { label: 'BATTLE STATIONS!', color: '#ff4444', icon: '\u2693', style: 'shake', priority: 3 },
        // Turn events: no overlay — status banner handles it
      };
      const anim = battleshipEvents[data.type];
      if (!anim) return;

      const now = Date.now();
      const priority = anim.priority || 1;
      if (priority < animPriorityRef.current && now < animExpiresRef.current) return;

      const duration = (data.duration || 3) * 1000;
      animPriorityRef.current = priority;
      animExpiresRef.current = now + duration;
      setActiveAnimation({ ...anim, key: now });
      if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
      animTimeoutRef.current = setTimeout(() => {
        setActiveAnimation(null);
        animPriorityRef.current = 0;
      }, duration);
    });

    // Handle registered confirmation
    socket.on('battleship:registered', (data) => {
      console.log('[Spectator] Registered with server', data);
      if (data.game) {
        setGameState(data.game);
      }
    });

    // Initial fetch
    fetchGameState();

    // Fallback polling (every 5 seconds) in case socket events are missed
    const fallbackInterval = setInterval(fetchGameState, 5000);

    return () => {
      socket.disconnect();
      clearInterval(fallbackInterval);
    };
  }, [fetchGameState, addRecentEvent]);

  // Get ships placed count for a team
  const getShipsPlaced = (team) => {
    if (!gameState?.teams?.[team]?.ships) return 0;
    return gameState.teams[team].ships.length;
  };

  // Check if team has all ships placed
  const isTeamReady = (team) => {
    return getShipsPlaced(team) >= TOTAL_SHIPS;
  };

  // Get ships remaining count for a team (without revealing positions)
  const getShipsRemaining = (team) => {
    if (!gameState?.teams?.[team]?.ships) return 0;
    return gameState.teams[team].ships.filter(s => !s.isSunk).length;
  };

  // Get total ship segments remaining
  const getHealthRemaining = (team) => {
    if (!gameState?.teams?.[team]?.ships) return 0;
    return gameState.teams[team].ships.reduce((total, ship) => {
      if (ship.isSunk) return total;
      return total + (ship.size - ship.hits);
    }, 0);
  };

  // Render setup phase team panel
  const renderSetupTeamPanel = (team) => {
    const shipsPlaced = getShipsPlaced(team);
    const ready = isTeamReady(team);

    return (
      <div
        className={`spectator-team-panel setup-phase ${ready ? 'ready' : 'waiting'}`}
        style={{ '--team-color': TEAM_COLORS[team] }}
      >
        <div className="team-header">
          <span className="team-name" style={{ color: TEAM_COLORS[team] }}>
            {TEAM_NAMES[team]}
          </span>
          {ready ? (
            <span className="ready-badge">LOCKED IN</span>
          ) : (
            <span className="waiting-badge">PLACING...</span>
          )}
        </div>

        <div className="setup-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${(shipsPlaced / TOTAL_SHIPS) * 100}%`,
                backgroundColor: TEAM_COLORS[team]
              }}
            />
          </div>
          <div className="progress-text">{shipsPlaced}/{TOTAL_SHIPS} Ships</div>
        </div>

        {ready && (
          <div className="ready-message">
            Positions Secured!
          </div>
        )}

        {!ready && (
          <div className="setup-grid-placeholder">
            <div className="placing-animation">
              <span className="ship-icon">🚢</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render a single team's grid (spectator view - no ship positions shown)
  const renderSpectatorGrid = (team) => {
    if (!gameState?.teams?.[team]) return null;

    const teamData = gameState.teams[team];
    const isEliminated = teamData.isEliminated;
    const isCurrentTurn = gameState.currentTurn === team;

    // Build a map of hits and misses from coordinate arrays
    const revealedCells = {};
    if (teamData.hits) {
      teamData.hits.forEach(({ x, y }) => { revealedCells[`${x},${y}`] = 'hit'; });
    }
    if (teamData.misses) {
      teamData.misses.forEach(({ x, y }) => { revealedCells[`${x},${y}`] = 'miss'; });
    }

    const columnLabels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

    return (
      <div
        className={`spectator-team-panel ${isEliminated ? 'eliminated' : ''} ${isCurrentTurn ? 'current-turn' : ''}`}
        style={{ '--team-color': TEAM_COLORS[team] }}
      >
        <div className="team-header">
          <span className="team-name" style={{ color: TEAM_COLORS[team] }}>
            {TEAM_NAMES[team]}
          </span>
          {isEliminated && <span className="eliminated-badge">ELIMINATED</span>}
          {isCurrentTurn && !isEliminated && <span className="turn-badge">ATTACKING</span>}
        </div>

        <div className="team-stats">
          <div className="stat">
            <span className="stat-label">Ships</span>
            <span className="stat-value">{getShipsRemaining(team)}/5</span>
          </div>
          <div className="stat">
            <span className="stat-label">HP</span>
            <span className="stat-value">{getHealthRemaining(team)}/{TOTAL_HEALTH}</span>
          </div>
          <div className="ship-health-bar">
            {teamData.ships?.map((ship, i) => (
              <span key={i} className={`ship-pip ${ship.isSunk ? 'sunk' : ship.hits > 0 ? 'damaged' : 'healthy'}`}
                title={`${ship.name} (${ship.size - ship.hits}/${ship.size})`}>
                {ship.name[0]}
              </span>
            ))}
          </div>
        </div>

        <div className="spectator-grid">
          <div className="grid-row header-row">
            <div className="grid-cell corner-cell"></div>
            {columnLabels.map(label => (
              <div key={label} className="grid-cell header-cell">{label}</div>
            ))}
          </div>

          {Array.from({ length: GRID_SIZE }, (_, y) => (
            <div key={y} className="grid-row">
              <div className="grid-cell header-cell">{GRID_SIZE - y}</div>
              {Array.from({ length: GRID_SIZE }, (_, x) => {
                const cellState = revealedCells[`${x},${y}`];
                return (
                  <div
                    key={x}
                    className={`grid-cell ${cellState || 'unknown'}`}
                  >
                    {cellState === 'hit' && <span className="cell-marker hit-marker">X</span>}
                    {cellState === 'miss' && <span className="cell-marker miss-marker">O</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="sunk-ships">
          {teamData.ships?.filter(s => s.isSunk).map((ship, idx) => (
            <div key={idx} className="sunk-ship">
              {ship.name}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render game status banner
  const renderStatusBanner = () => {
    if (!gameState) return null;

    const teamsReady = TEAMS.filter(t => isTeamReady(t)).length;

    if (gameState.phase === 'setup') {
      return (
        <div className="status-banner setup">
          <span className="status-icon">&#9881;</span>
          <span className="status-text">
            SETUP PHASE - {teamsReady}/4 Teams Ready
          </span>
          {teamsReady === 4 && (
            <span className="all-ready-text">All positions locked in!</span>
          )}
        </div>
      );
    }

    if (gameState.phase === 'finished') {
      return (
        <div className="status-banner finished">
          <span className="status-icon">&#127942;</span>
          <span className="status-text">
            GAME OVER - <span style={{ color: TEAM_COLORS[gameState.winner] }}>{TEAM_NAMES[gameState.winner]}</span> WINS!
          </span>
        </div>
      );
    }

    // Playing phase
    const currentTeam = gameState.currentTurn;
    return (
      <div className="status-banner playing">
        <span className="status-text">
          <span style={{ color: TEAM_COLORS[currentTeam] }}>{TEAM_NAMES[currentTeam]}</span>
          <span className="turn-action"> IS ATTACKING</span>
        </span>
        <div className="turn-indicator" style={{ backgroundColor: TEAM_COLORS[currentTeam] }}></div>
      </div>
    );
  };

  // Render recent events
  const renderRecentEvents = () => {
    const events = recentEvents.length > 0 ? recentEvents : (gameState?.log?.slice(-5).reverse() || []);
    if (events.length === 0) return null;

    return (
      <div className="game-log">
        {events.map((entry, idx) => (
          <div key={idx} className="log-entry" style={{ opacity: 1 - (idx * 0.12) }}>
            {entry.message}
          </div>
        ))}
      </div>
    );
  };

  // Render connection status
  const renderConnectionStatus = () => {
    return (
      <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
        <span className="connection-dot"></span>
        <span className="connection-text">{connected ? 'LIVE' : 'RECONNECTING...'}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="spectator-page loading">
        {!isEmbed && !isFs && (
          <button onClick={() => document.documentElement.requestFullscreen()} style={{
            position: 'fixed', top: 8, left: 8, zIndex: 99999,
            background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
            color: '#00ff41', padding: '4px 10px', fontSize: '11px',
            fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
          }}>FULLSCREEN</button>
        )}
        {!isEmbed && (
          <div className="spectator-back-nav">
            <Link to="/spectate" className="back-link">← Back to Spectate</Link>
          </div>
        )}
        <div className="loading-spinner"></div>
        <div className="loading-text">Connecting to Portfolio Warfare...</div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="spectator-page no-game">
        {!isEmbed && !isFs && (
          <button onClick={() => document.documentElement.requestFullscreen()} style={{
            position: 'fixed', top: 8, left: 8, zIndex: 99999,
            background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
            color: '#00ff41', padding: '4px 10px', fontSize: '11px',
            fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
          }}>FULLSCREEN</button>
        )}
        {!isEmbed && (
          <div className="spectator-back-nav">
            <Link to="/spectate" className="back-link">← Back to Spectate</Link>
          </div>
        )}
        {renderConnectionStatus()}
        <div className="no-game-icon">&#9875;</div>
        <div className="no-game-text">No Active Game</div>
        <div className="no-game-subtext">Waiting for game to start...</div>
        <div className="waiting-animation">
          <span className="waiting-dot"></span>
          <span className="waiting-dot"></span>
          <span className="waiting-dot"></span>
        </div>
      </div>
    );
  }

  return (
    <div className={`spectator-page ${compact ? 'spectator-compact' : ''}`} style={{ position: 'relative' }}>
      {!isEmbed && !isFs && (
        <button onClick={() => document.documentElement.requestFullscreen()} style={{
          position: 'fixed', top: 8, left: 8, zIndex: 99999,
          background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
          color: '#00ff41', padding: '4px 10px', fontSize: '11px',
          fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
        }}>FULLSCREEN</button>
      )}
      {!isEmbed && (
        <div className="spectator-back-nav">
          <Link to="/spectate" className="back-link">← Back to Spectate</Link>
        </div>
      )}
      {!compact && renderConnectionStatus()}

      <div className="spectator-header">
        <h1 className="game-title">PORTFOLIO WARFARE</h1>
        {renderStatusBanner()}
      </div>

      <div className="spectator-board">
        <div className="board-row">
          {gameState.phase === 'setup' ? renderSetupTeamPanel('red') : renderSpectatorGrid('red')}
          {gameState.phase === 'setup' ? renderSetupTeamPanel('blue') : renderSpectatorGrid('blue')}
        </div>
        <div className="board-row">
          {gameState.phase === 'setup' ? renderSetupTeamPanel('green') : renderSpectatorGrid('green')}
          {gameState.phase === 'setup' ? renderSetupTeamPanel('yellow') : renderSpectatorGrid('yellow')}
        </div>
      </div>

      {renderRecentEvents()}

      {activeAnimation && (
        <div key={activeAnimation.key} className="spectator-anim-overlay" style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', zIndex: 9999,
          animation: `golfFade ${activeAnimation.style === 'explode' ? '4s' : '3s'} ease-out forwards`,
          background: activeAnimation.style === 'explode' ? `radial-gradient(circle, ${activeAnimation.color}22 0%, transparent 70%)` : 'transparent',
        }}>
          <div className="spectator-anim-icon" style={{
            fontSize: activeAnimation.style === 'explode' ? '160px' : '120px',
            animation: `golf_${activeAnimation.style || 'bounce'} 0.8s ease-out`,
          }}>{activeAnimation.icon}</div>
          <div className="spectator-anim-label" style={{
            fontSize: activeAnimation.label.length > 16 ? '48px' : '64px',
            fontWeight: 'bold',
            color: activeAnimation.color,
            fontFamily: 'Courier New, monospace',
            textShadow: `0 0 30px ${activeAnimation.color}, 0 0 60px ${activeAnimation.color}`,
            letterSpacing: '8px',
            animation: `golf_${activeAnimation.style || 'bounce'}_text 0.6s ease-out`,
          }}>{activeAnimation.label}</div>
        </div>
      )}

      {!compact && (
        <div className="spectator-footer">
          <span className="update-time">
            Last updated: {lastUpdate?.toLocaleTimeString()}
          </span>
        </div>
      )}

      <style>{`
        @keyframes golfFade {
          0% { opacity: 0; }
          10% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes golf_bounce {
          0% { transform: scale(0) translateY(50px); }
          40% { transform: scale(1.3) translateY(-30px); }
          60% { transform: scale(0.9) translateY(10px); }
          80% { transform: scale(1.1) translateY(-5px); }
          100% { transform: scale(1) translateY(0); }
        }
        @keyframes golf_bounce_text {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes golf_shake {
          0% { transform: scale(0) rotate(0deg); }
          20% { transform: scale(1.2) rotate(-10deg); }
          40% { transform: scale(1) rotate(10deg); }
          60% { transform: scale(1.1) rotate(-5deg); }
          80% { transform: scale(1) rotate(3deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes golf_shake_text {
          0% { transform: translateX(-20px); opacity: 0; }
          30% { transform: translateX(10px); opacity: 1; }
          60% { transform: translateX(-5px); }
          100% { transform: translateX(0); }
        }
        @keyframes golf_explode {
          0% { transform: scale(0); }
          30% { transform: scale(2); }
          50% { transform: scale(0.8); }
          70% { transform: scale(1.5); }
          100% { transform: scale(1); }
        }
        @keyframes golf_explode_text {
          0% { transform: scale(0); opacity: 0; letter-spacing: 0px; }
          50% { transform: scale(1.3); opacity: 1; letter-spacing: 20px; }
          100% { transform: scale(1); letter-spacing: 8px; }
        }
        @keyframes golf_fade {
          0% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes golf_fade_text {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default BattleshipSpectatorPage;
